import React, { useState, useEffect } from 'react';
import './TopicManager.css';
import './QuickPost.css';

function QuickPost({ user, onClose, showHeader = true }) {
  const [sites, setSites] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [progressSteps, setProgressSteps] = useState([
    { id: 1, label: 'Creating topic...', status: 'pending' },
    { id: 2, label: 'Generating AI content...', status: 'pending' },
    { id: 3, label: 'Publishing to WordPress...', status: 'pending' },
    { id: 4, label: 'Complete!', status: 'pending' }
  ]);
  const [error, setError] = useState(null);
  const [currentTopicId, setCurrentTopicId] = useState(null);
  const [selectedPublishType, setSelectedPublishType] = useState('draft');
  const [formData, setFormData] = useState({
  topic_title: '',
  wordpress_site_id: '',
  category_id: '',
  publish_type: 'draft',
  schedule_type: 'now',
  scheduled_at: ''
  });
  const [showAddSiteForm, setShowAddSiteForm] = useState(false);
  const [newSiteData, setNewSiteData] = useState({
  site_name: '',
  site_url: '',
  api_key: ''
});

  useEffect(() => {
    loadWordPressSites();
  }, []);

  const loadWordPressSites = async () => {
    try {
      const response = await fetch('https://contentvertex.zealcart.com/api/wordpress-sites', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setSites(data.sites);
      }
    } catch (error) {
      console.error('Failed to load sites:', error);
    }
  };

  const loadCategories = async (siteId) => {
    try {
      const response = await fetch(`https://contentvertex.zealcart.com/api/wordpress-sites/${siteId}/categories`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setCategories(prev => ({
          ...prev,
          [siteId]: data.categories
        }));
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const handleSiteChange = async (siteId) => {
    setFormData({...formData, wordpress_site_id: siteId, category_id: ''});
    if (siteId) {
      await loadCategories(siteId);
    }
  };

  const updateStepStatus = (stepId, status, errorMsg = null) => {
    setProgressSteps(prev => prev.map(step => 
      step.id === stepId ? { ...step, status, error: errorMsg } : step
    ));
  };

  const updateProgressSteps = (publishType, scheduleType) => {
  if (scheduleType === 'scheduled') {
    const actionLabel = publishType === 'draft' ? 'Scheduling as Draft...' : 'Scheduling as Live Post...';
    const successLabel = publishType === 'draft' ? 'Scheduled as Draft!' : 'Scheduled as Live Post!';
    
    setProgressSteps([
      { id: 1, label: 'Creating topic...', status: 'pending' },
      { id: 2, label: actionLabel, status: 'pending' },
      { id: 3, label: successLabel, status: 'pending' },
      { id: 4, label: 'Complete!', status: 'pending' }
    ]);
  } else if (publishType === 'draft') {
    setProgressSteps([
      { id: 1, label: 'Creating topic...', status: 'pending' },
      { id: 2, label: 'Generating AI content...', status: 'pending' },
      { id: 3, label: 'Saving as Draft...', status: 'pending' },
      { id: 4, label: 'Complete!', status: 'pending' }
    ]);
  } else {
    setProgressSteps([
      { id: 1, label: 'Creating topic...', status: 'pending' },
      { id: 2, label: 'Generating AI content...', status: 'pending' },
      { id: 3, label: 'Publishing to WordPress...', status: 'pending' },
      { id: 4, label: 'Complete!', status: 'pending' }
    ]);
  }
};

  const handlePost = async (e) => {
  e.preventDefault();
  setLoading(true);
  setShowProgress(true);
  setError(null);
  setSelectedPublishType(formData.publish_type);

  // Update progress steps based on publish type and schedule
  updateProgressSteps(formData.publish_type, formData.schedule_type);
  
  // Set first step to loading
  updateStepStatus(1, 'loading');

  try {
    // Add user's timezone to the form data
    const formDataWithTimezone = {
      ...formData,
      user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };

    // Step 1: Create topic
    const response = await fetch('https://contentvertex.zealcart.com/api/topics', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formDataWithTimezone)
    });

    const data = await response.json();
    
    if (!data.success) {
      updateStepStatus(1, 'error', data.message || 'Failed to create topic');
      setError(data.message || 'Failed to create topic');
      setLoading(false);
      return;
    }

    const newTopic = data.topic;
    setCurrentTopicId(newTopic.id);
    updateStepStatus(1, 'complete');
    
    // Step 2: Always process (generate content) regardless of schedule type
    updateStepStatus(2, 'loading');
    
    const processResponse = await fetch(`https://contentvertex.zealcart.com/api/topics/${newTopic.id}/process`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      }
    });

    if (!processResponse.ok) {
      updateStepStatus(2, 'error', 'Failed to start content generation');
      setError('Failed to start content generation');
      setLoading(false);
      return;
    }

    // Poll for completion with both publish type and schedule type
    await pollTopicStatus(newTopic.id, formData.publish_type, formData.schedule_type);

  } catch (error) {
    console.error('Error:', error);
    setError('An unexpected error occurred: ' + error.message);
    setLoading(false);
  }
};

  const pollTopicStatus = async (topicId, publishType, scheduleType) => {
  const maxAttempts = 60;
  let attempts = 0;

  const checkStatus = async () => {
    try {
      const response = await fetch(`https://contentvertex.zealcart.com/api/topics/${topicId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const topic = data.topic;

        // Add this debug line temporarily
      console.log('Polling status:', topic.status, 'Schedule type:', scheduleType);

        // For scheduled posts, complete when content is generated (not published)
        if (scheduleType === 'scheduled' && topic.status === 'generated') {
          updateStepStatus(2, 'complete');
          updateStepStatus(3, 'complete');
          updateStepStatus(4, 'complete');
          setLoading(false);
          
          setTimeout(() => {
            setShowProgress(false);
            if (onClose) onClose('topics');
          }, 2000);
          return;
        }

        // For immediate posts, complete when published
        if (scheduleType === 'now' && topic.status === 'published') {
          updateStepStatus(2, 'complete');
          updateStepStatus(3, 'complete');
          updateStepStatus(4, 'complete');
          setLoading(false);
          
          setTimeout(() => {
            setShowProgress(false);
            if (onClose) onClose('topics');
          }, 2000);
          return;
        }

        if (topic.status === 'failed') {
          updateStepStatus(2, 'error', topic.error_message || 'Content generation failed');
          setError(topic.error_message || 'Content generation failed');
          setLoading(false);
          return;
        }

        if (topic.status === 'processing') {
          updateStepStatus(2, 'loading');
        }

        if (topic.status === 'generated' && scheduleType === 'now') {
          // For immediate posts, auto-publish after generation
          updateStepStatus(2, 'complete');
          updateStepStatus(3, 'loading');
          
          try {
            const publishResponse = await fetch(`https://contentvertex.zealcart.com/api/topics/${topicId}/publish`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ publish_type: publishType })
            });
            
            if (!publishResponse.ok) {
              updateStepStatus(3, 'error', 'Failed to publish to WordPress');
              setError('Failed to publish to WordPress');
              setLoading(false);
              return;
            }
          } catch (publishError) {
            updateStepStatus(3, 'error', 'Publishing error occurred');
            setError('Publishing error occurred');
            setLoading(false);
            return;
          }
        }

        // Continue polling
        if (attempts < maxAttempts && 
            !((scheduleType === 'scheduled' && topic.status === 'generated') || 
              (scheduleType === 'now' && topic.status === 'published')) && 
            topic.status !== 'failed') {
          attempts++;
          setTimeout(checkStatus, 10000);
        } else if (attempts >= maxAttempts) {
          updateStepStatus(2, 'error', 'Processing timeout - please check Topic Queue');
          setError('Processing is taking longer than expected. Check Topic Queue for status.');
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Polling error:', error);
      updateStepStatus(2, 'error', 'Failed to check status');
      setError('Failed to check processing status');
      setLoading(false);
    }
  };

  setTimeout(checkStatus, 5000);
};

  const handleRetry = () => {
    setError(null);
    setShowProgress(false);
    
    if (currentTopicId) {
      // Retry processing the existing topic
      setShowProgress(true);
      updateProgressSteps(selectedPublishType);
      updateStepStatus(1, 'complete');
      updateStepStatus(2, 'loading');
      
      fetch(`https://contentvertex.zealcart.com/api/topics/${currentTopicId}/process`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      }).then(() => pollTopicStatus(currentTopicId, selectedPublishType));
    }
  };

  const organizeCategories = (categories) => {
    const organized = [];
    const categoryMap = {};
    
    categories.forEach(cat => {
      categoryMap[cat.id] = { ...cat, children: [] };
    });
    
    categories.forEach(cat => {
      if (cat.parent === 0) {
        organized.push(categoryMap[cat.id]);
      } else {
        if (categoryMap[cat.parent]) {
          categoryMap[cat.parent].children.push(categoryMap[cat.id]);
        }
      }
    });
    
    return organized;
  };

  const renderCategoryOptions = (categories, level = 0) => {
    const options = [];
    const prefix = '—'.repeat(level);
    
    categories.forEach(category => {
      options.push(
        <option key={category.id} value={category.id}>
          {prefix} {category.name}
        </option>
      );
      
      if (category.children && category.children.length > 0) {
        options.push(...renderCategoryOptions(category.children, level + 1));
      }
    });
    
    return options;
  };

  const handleAddNewSite = async (e) => {
  e.preventDefault();
  try {
    const response = await fetch('https://contentvertex.zealcart.com/api/wordpress-sites', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(newSiteData)
    });

    const data = await response.json();
    if (data.success) {
      // Refresh sites list
      await loadWordPressSites();
      // Select the new site
      setFormData({...formData, wordpress_site_id: data.site.id});
      // Clear form and hide it
      setNewSiteData({site_name: '', site_url: '', api_key: ''});
      setShowAddSiteForm(false);
      alert('WordPress site added successfully!');
    } else {
      alert('Failed to add site: ' + data.message);
    }
  } catch (error) {
    alert('Failed to add WordPress site');
  }
};

  return (
    <div className="quickpost-wrapper" style={{minHeight: 'auto', height: 'auto'}}>
    <div className="topic-manager" style={{maxWidth: '600px', margin: showHeader ? '40px auto' : '0 auto'}}>
  {showHeader && (
    <div className="manager-header">
      <h2>Generate & Post Article</h2>
      {onClose && (
        <button onClick={onClose} className="btn btn-secondary">
          ← Back to Dashboard
        </button>
      )}
    </div>
  )}

      {!showProgress ? (
          <div className="modal-content" style={{boxShadow: 'none', padding: '24px', position: 'relative'}}>
            {onClose && (
              <button 
                type="button"
                onClick={() => onClose()}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#64748b',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10
                }}
              >
                ×
              </button>
            )}
            <form onSubmit={handlePost}>
            <div className="form-group">
              <label>Topic Title</label>
              <input
                type="text"
                placeholder="e.g., Benefits of meditation for stress relief"
                value={formData.topic_title}
                onChange={(e) => setFormData({...formData, topic_title: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
  <label>WordPress Site</label>
  <div style={{display: 'flex', gap: '8px', alignItems: 'flex-end'}}>
    <div style={{flex: 1}}>
      <select
        value={formData.wordpress_site_id}
        onChange={(e) => handleSiteChange(e.target.value)}
        required
      >
        <option value="">Select a site</option>
        {sites.map(site => (
          <option key={site.id} value={site.id}>{site.site_name}</option>
        ))}
      </select>
    </div>
    <button
      type="button"
      onClick={() => setShowAddSiteForm(!showAddSiteForm)}
      style={{
        padding: '12px 16px',
        background: '#f3f4f6',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '14px',
        color: '#6b7280'
      }}
    >
      + Add Site
    </button>
  </div>
  
  {/* Mini Add Site Form */}
  {showAddSiteForm && (
    <div style={{
      marginTop: '16px',
      padding: '16px',
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: '8px'
    }}>
      <h4 style={{margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600'}}>Add New WordPress Site</h4>
      <form onSubmit={handleAddNewSite}>
        <div style={{marginBottom: '12px'}}>
          <input
            type="text"
            placeholder="Site Name (e.g., My Blog)"
            value={newSiteData.site_name}
            onChange={(e) => setNewSiteData({...newSiteData, site_name: e.target.value})}
            required
            style={{width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px'}}
          />
        </div>
        <div style={{marginBottom: '12px'}}>
          <input
            type="url"
            placeholder="Site URL (e.g., https://myblog.com)"
            value={newSiteData.site_url}
            onChange={(e) => setNewSiteData({...newSiteData, site_url: e.target.value})}
            required
            style={{width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px'}}
          />
        </div>
        <div style={{marginBottom: '12px'}}>
          <input
            type="text"
            placeholder="ContentVertex API Key"
            value={newSiteData.api_key}
            onChange={(e) => setNewSiteData({...newSiteData, api_key: e.target.value})}
            required
            style={{width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px'}}
          />
        </div>
        <div style={{display: 'flex', gap: '8px'}}>
          <button
            type="submit"
            style={{
              padding: '8px 16px',
              background: '#6366f1',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            Add Site
          </button>
          <button
            type="button"
            onClick={() => setShowAddSiteForm(false)}
            style={{
              padding: '8px 16px',
              background: '#f3f4f6',
              color: '#6b7280',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  )}
</div>

            {formData.wordpress_site_id && categories[formData.wordpress_site_id] && (
              <div className="form-group">
                <label>Category</label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                >
                  <option value="">Uncategorized</option>
                  {renderCategoryOptions(organizeCategories(categories[formData.wordpress_site_id]))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Publish Type</label>
              <select
                value={formData.publish_type}
                onChange={(e) => setFormData({...formData, publish_type: e.target.value})}
              >
                <option value="draft">Draft</option>
                <option value="publish">Publish Immediately</option>
              </select>
            </div>

            <div className="form-group">
              <label>Schedule</label>
              <select
                value={formData.schedule_type || 'now'}
                onChange={(e) => setFormData({...formData, schedule_type: e.target.value})}
              >
                <option value="now">Post Now</option>
                <option value="scheduled">Schedule for Later</option>
              </select>
            </div>

            {formData.schedule_type === 'scheduled' && (
              <div className="form-group">
                <label>Schedule Date & Time</label>
                <input
                  type="datetime-local"
                  value={formData.scheduled_at || ''}
                  onChange={(e) => setFormData({...formData, scheduled_at: e.target.value})}
                  min={new Date(Date.now() + 60 * 1000).toISOString().slice(0, 16)} // Min 1 minute from now
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                <small style={{color: '#6b7280', fontSize: '12px'}}>
                  Minimum scheduling time is 1 minute from now (For Testing)
                </small>
              </div>
            )}

            <div className="form-actions">
              {onClose && <button type="button" onClick={onClose}>Cancel</button>}
              <button type="submit" disabled={loading} className="btn btn-primary">
                {loading ? 'Posting...' : 'Post'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="quickpost-container">
          <div className="progress-content">
            <h3>Processing Article</h3>
            <button 
              type="button"
              onClick={() => {
                setShowProgress(false);
                setLoading(false);
                if (onClose) onClose();
              }}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#64748b',
                padding: '0',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}
            >
              ×
            </button>
            <div className="progress-steps">
              {progressSteps.map(step => (
                <div key={step.id} className={`progress-step ${step.status}`}>
                  <div className="step-icon">
                    {step.status === 'complete' && <span className="checkmark">✓</span>}
                    {step.status === 'loading' && <span className="spinner"></span>}
                    {step.status === 'error' && <span className="error-icon">✕</span>}
                    {step.status === 'pending' && <span className="pending-icon">○</span>}
                  </div>
                  <div className="step-label">
                    {step.label}
                    {step.error && <div className="step-error">{step.error}</div>}
                  </div>
                </div>
              ))}
            </div>

            {error && (
              <div className="error-section">
                <p className="error-message">{error}</p>
                <button onClick={handleRetry} className="btn btn-primary">
                  Retry
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

export default QuickPost;